// onboarding checklist for new serviceprovider users, see useOnboarding and the
// GET /serviceprovider/:serviceproviderUUID/onboarding + POST /user/onboarding routes

// must match ONBOARDING_STEP_IDS of the api, 'welcome' marks the welcome modal as seen
export type OnboardingStepId = 'welcome' | 'invite-team' | 'modes' | 'create-nfr' | 'connect-entra' | 'install-collector'

export type OnboardingStatus = {
  teamInvited: boolean;
  nfrClientCreated: boolean;
  entraConnected: boolean;
  collectorConnected: boolean;
  firstClientUUID: string | null;
}

export type OnboardingTourStep = {
  // data-tour attribute of the highlighted element
  target: string;
  title: string;
  description: string;
}

export type OnboardingStep = {
  id: OnboardingStepId;
  title: string;
  description: string;
  // page the tour runs on, null while the step is blocked
  route: string | null;
  tour: OnboardingTourStep[];
  done: boolean;
  // the step needs a monitoring client first (entra / collector)
  blocked: boolean;
}

export function buildOnboardingSteps (
  serviceproviderUUID: string,
  status: OnboardingStatus | null,
  dismissedSteps: string[]
): OnboardingStep[] {
  const isDismissed = (id: OnboardingStepId): boolean => dismissedSteps.includes(id)
  const clientSettings = status?.firstClientUUID ? `/client/${status.firstClientUUID}/settings` : null

  return [
    {
      id: 'invite-team',
      title: 'Kollegen einladen',
      description: 'Laden Sie Ihr Team ein, damit alle Zugriff auf Ihre Kunden haben.',
      route: `/provider/${serviceproviderUUID}/settings`,
      tour: [{
        target: 'team',
        title: 'Teamverwaltung',
        description: 'Hier laden Sie Kollegen per E-Mail ein. Team-Mitglieder sehen alle Kunden und Einstellungen.'
      }],
      done: !!status?.teamInvited || isDismissed('invite-team'),
      blocked: false
    },
    {
      id: 'modes',
      title: 'Die drei Modi kennenlernen',
      description: 'Monitoring, Audit und Quickcheck im Überblick.',
      // the modes are explained in the serviceprovider navigation
      route: `/provider/${serviceproviderUUID}/monitoring`,
      tour: [
        {
          target: 'nav-monitoring',
          title: 'Monitoring',
          description: 'Dauerhafte Überwachung von Active Directory und Entra ID: tägliche Schwachstellen-Analysen (IoE) und Angriffserkennung (IoA) in Echtzeit, monatlich abgerechnet.'
        },
        {
          target: 'nav-audit',
          title: 'Audit',
          description: 'Einmalige, tiefgehende Analyse einer Umgebung, z. B. für ein Projekt oder einen Report beim Kunden.'
        },
        {
          target: 'nav-quickcheck',
          title: 'Quickcheck',
          description: 'Kostenlose Kurzanalyse für Interessenten, auf Wunsch als Whitelabel-Formular auf Ihrer Webseite. Ideal zur Lead-Gewinnung.'
        }
      ],
      done: isDismissed('modes'),
      blocked: false
    },
    {
      id: 'create-nfr',
      title: 'NFR-Domäne anlegen',
      description: 'Legen Sie Ihre eigene Domäne kostenlos als NFR-Monitoring an.',
      route: `/provider/${serviceproviderUUID}/monitoring`,
      tour: [
        {
          target: 'nfr-seats',
          title: 'Kostenlose NFR-Lizenzen',
          description: 'Sie haben zwei kostenlose Not-for-Resale-Lizenzen für Ihre eigene Umgebung.'
        },
        {
          target: 'new-client',
          title: 'Domäne anlegen',
          description: 'Klicken Sie hier und setzen Sie den Haken „Dies ist eine NFR-Domäne“.'
        }
      ],
      done: !!status?.nfrClientCreated || isDismissed('create-nfr'),
      blocked: false
    },
    {
      id: 'connect-entra',
      title: 'Entra ID verbinden',
      description: 'Erteilen Sie die Admin-Zustimmung für Ihren Microsoft-Tenant.',
      route: clientSettings,
      tour: [{
        target: 'entra',
        title: 'Entra ID',
        description: 'Tenant-ID eintragen und den Consent-Link mit einem Global Admin bestätigen. Die Synchronisierung startet danach automatisch.'
      }],
      done: !!status?.entraConnected || isDismissed('connect-entra'),
      blocked: !clientSettings
    },
    {
      id: 'install-collector',
      title: 'Collector installieren',
      description: 'Installieren Sie den Collector im Active Directory.',
      route: clientSettings,
      tour: [{
        target: 'collector',
        title: 'Collector',
        description: 'Laden Sie den Collector herunter und installieren Sie ihn auf einem Server in der Domäne. Die Installationsanleitung ist oben verlinkt.'
      }],
      done: !!status?.collectorConnected || isDismissed('install-collector'),
      blocked: !clientSettings
    }
  ]
}
